import React, { useEffect, useState } from 'react';

import { useNavigate, useParams, useResolvedPath } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfilePlacementsResponse } from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';
import { ignoreCancelError } from 'src/utils/apiClient';
import { type RestrictionListParentTask, type RestrictionListTask } from 'src/utils/restrictionUtilsLegacy';

import ActivePlacementCard from './activePlacementCard';
import PendingPlacementCard from './pendingPlacementCard';
import TransferredInPlacementCard from './transferredInPlacementCard';

type PageState = DataFetchingState<{
  placements: AssociateProfilePlacementsResponse;
}>;

const DEFAULT_PAGE_STATE: PageState = {
  status: 'loading',
} as const;

interface AssociateProfilePlacementsProps {
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
}

const AssociateProfilePlacements = ({ regionTasks }: AssociateProfilePlacementsProps) => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);
  const [refetchKey, setRefetchKey] = useState(0);

  const onTransferSuccess = () => setRefetchKey((k) => k + 1);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchPlacements = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        setPageState({ status: 'error' });
        return;
      }

      try {
        const { data: placements } = await ignoreCancelError(
          associateProfileService.getAssociateProfilePlacements(alias, selectedSite.id, { signal }),
          signal,
        );

        setPageState({ status: 'success', data: { placements } });
      } catch (error) {
        console.error('Failed to fetch associate profile placements', error);
        setPageState({ status: 'error' });
      }
    };

    fetchPlacements();

    return () => {
      controller.abort();
    };
  }, [alias, selectedSite, refetchKey]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  if (
    pageState.data.placements.pending.length === 0 &&
    pageState.data.placements.active.length === 0 &&
    pageState.data.placements.transferredIn.length === 0
  ) {
    return <NoPlacements alias={alias!} siteId={selectedSite.id} siteName={selectedSite.site} />;
  }

  return (
    <Column spacing="400">
      <Text type="b500">
        Placements Pending Action (
        {pageState.data.placements.pending.length + pageState.data.placements.transferredIn.length})
      </Text>
      {pageState.data.placements.pending.map((placement) => (
        <PendingPlacementCard
          key={placement.accommodationId}
          placement={placement}
          regionTasks={regionTasks}
          onTransferSuccess={onTransferSuccess}
        />
      ))}
      {pageState.data.placements.transferredIn.map((placement) => (
        <TransferredInPlacementCard key={placement.accommodationId} placement={placement} />
      ))}
      <Text type="b500">Active Placements ({pageState.data.placements.active.length})</Text>
      {pageState.data.placements.active.map((placement) => (
        <ActivePlacementCard
          key={placement.accommodationId}
          placement={placement}
          regionTasks={regionTasks}
          onTransferSuccess={onTransferSuccess}
        />
      ))}
    </Column>
  );
};

const NoPlacements = ({ alias, siteId, siteName }: { alias: string; siteId: number; siteName: string }) => {
  const navigate = useNavigate();
  const [lastPlacementDate, setLastPlacementDate] = useState<string | null>(null);
  const pathObj = useResolvedPath('..');

  useEffect(() => {
    const controller = new AbortController();

    const fetchLastPlacementDate = async () => {
      try {
        const response = await ignoreCancelError(
          associateProfileService.getLastPlacementDate(alias, siteId, { signal: controller.signal }),
          controller.signal,
        );
        setLastPlacementDate(response.data);
      } catch (error) {
        console.error('Failed to fetch associate last placement date', error);
      }
    };

    fetchLastPlacementDate();

    return () => {
      controller.abort();
    };
  }, [alias, siteId]);

  return (
    <Column>
      <Text>There are no placements active or pending action at site: {siteName}</Text>
      {lastPlacementDate !== null && (
        <Row>
          <Text>
            Last placement closed on <strong>{moment(lastPlacementDate).format('MM/DD/YYYY')}</strong>.
          </Text>
          <Link type="secondary" href={`${pathObj.pathname}/history`} onClick={navigate}>
            View History
          </Link>
        </Row>
      )}
    </Column>
  );
};

export default AssociateProfilePlacements;
